import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Tag,
  Switch,
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Tooltip,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { recurringPOAPI } from '../../api/config';

const { Text, Title } = Typography;
const { Option } = Select;

const fmtDate  = (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—');
const fmtRelative = (v) => {
  if (!v) return '—';
  const d = dayjs(v);
  const now = dayjs();
  const diff = d.diff(now, 'day');
  if (diff < 0)  return <Text type="danger">Overdue</Text>;
  if (diff === 0) return <Text style={{ color: '#faad14' }}>Today</Text>;
  if (diff === 1) return <Text style={{ color: '#1890ff' }}>Tomorrow</Text>;
  return <Text style={{ color: '#52c41a' }}>In {diff} days</Text>;
};

const RecurringPOList = () => {
  const { user } = useSelector((s) => s.auth);
  const navigate  = useNavigate();

  const [records, setRecords]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);

  // Create form state
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const [createLoading, setCreateLoading] = useState(false);
  const { vendors } = useSelector((s) => s.vendors || {});
  const { products } = useSelector((s) => s.products || {});
  const { delivery_addresses, firm_names } = useSelector((s) => s.home || {});
  const [frequency, setFrequency] = useState('monthly');
  const [items, setItems] = useState([{ key: Date.now(), product_id: undefined, product_name: '', quantity: 1, rate: 0, tax_slab: '18', discount: 0 }]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await recurringPOAPI.list(activeOnly);
      setRecords(res.data || []);
    } catch {
      message.error('Failed to load recurring purchase orders');
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  useEffect(() => { load(); }, [activeOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = async (id, checked) => {
    try {
      await recurringPOAPI.update(id, { is_active: checked });
      message.success(`Recurring PO ${checked ? 'activated' : 'paused'}`);
      load();
    } catch {
      message.error('Failed to update status');
    }
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setCreateLoading(true);
      const addr = (delivery_addresses || []).find(a => a.id === values.delivery_address_id);
      const payload = {
        name:               values.name,
        vendor_id:          values.vendor_id,
        firm_name:          values.firm_name,
        delivery_address:   addr ? `${addr.address}, ${addr.city}` : '',
        delivery_address_id: values.delivery_address_id,
        frequency:          values.frequency,
        day_of_week:        values.day_of_week || 0,
        day_of_month:       values.day_of_month || 1,
        interval_days:      values.interval_days || 7,
        items: items
          .filter(i => i.product_id && i.quantity > 0 && i.rate > 0)
          .map(i => ({
            product_id:   i.product_id,
            product_name: i.product_name,
            unit:         i.unit || '',
            quantity:     i.quantity,
            rate:         i.rate,
            tax_slab:     i.tax_slab,
            discount:     i.discount,
          })),
      };
      await recurringPOAPI.create(payload);
      message.success('Recurring PO template created');
      setCreateOpen(false);
      form.resetFields();
      setItems([{ key: Date.now(), product_id: undefined, product_name: '', quantity: 1, rate: 0, tax_slab: '18', discount: 0 }]);
      load();
    } catch (e) {
      const msg = e.response?.data?.error || 'Failed to create';
      message.error(msg);
    } finally {
      setCreateLoading(false);
    }
  };

  const updateItem = (key, field, value) =>
    setItems(prev => prev.map(i => i.key === key ? { ...i, [field]: value } : i));

  const activeCount = records.filter(r => r.is_active).length;
  const overdueCount = records.filter(r => r.next_run_at && dayjs(r.next_run_at).isBefore(dayjs())).length;

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      width: 110,
      render: (v, r) => {
        const details = v === 'weekly'
          ? `Every ${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][r.day_of_week]}`
          : v === 'monthly'
          ? `Day ${r.day_of_month} of month`
          : `Every ${r.interval_days} days`;
        return (
          <Tooltip title={details}>
            <Tag color="geekblue">{v}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Next Run',
      dataIndex: 'next_run_at',
      key: 'next_run',
      width: 120,
      render: (v) => fmtRelative(v),
      sorter: (a, b) => new Date(a.next_run_at) - new Date(b.next_run_at),
    },
    {
      title: 'Last Run',
      dataIndex: 'last_run_at',
      key: 'last_run',
      width: 110,
      render: fmtDate,
    },
    {
      title: 'Total Runs',
      dataIndex: 'run_count',
      key: 'runs',
      width: 90,
      align: 'center',
      render: (v) => <Tag>{v || 0}</Tag>,
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'active',
      width: 90,
      align: 'center',
      render: (v, record) => (
        <Switch
          checked={v}
          onChange={(checked) => handleToggle(record.id, checked)}
          checkedChildren="ON"
          unCheckedChildren="OFF"
        />
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-content">
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><SyncOutlined /></div>
            <div className="page-header-text">
              <h1>Recurring Purchase Orders</h1>
              <p>Automated PO templates — run on a schedule (Admin only)</p>
            </div>
          </div>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic title="Total Templates" value={records.length}
                prefix={<ReloadOutlined style={{ color: '#1890ff' }} />} valueStyle={{ color: '#1890ff' }} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
              <Statistic title="Active" value={activeCount}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} valueStyle={{ color: '#52c41a' }} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ background: overdueCount > 0 ? '#fff2f0' : undefined, borderColor: overdueCount > 0 ? '#ffccc7' : undefined }}>
              <Statistic title="Overdue" value={overdueCount}
                valueStyle={{ color: overdueCount > 0 ? '#ff4d4f' : '#52c41a' }} />
            </Card>
          </Col>
        </Row>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Space>
            <Switch checked={activeOnly} onChange={setActiveOnly} checkedChildren="Active only" unCheckedChildren="All" />
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New Recurring PO
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: false }}
          locale={{
            emptyText: (
              <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
                No recurring PO templates. Create one to automate regular orders.
              </div>
            ),
          }}
        />
      </div>

      {/* Create Modal */}
      <Modal
        title="New Recurring PO Template"
        open={createOpen}
        onCancel={() => { setCreateOpen(false); form.resetFields(); }}
        onOk={handleCreate}
        okText="Create Template"
        confirmLoading={createLoading}
        width={760}
        destroyOnClose
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical" initialValues={{ frequency: 'monthly', day_of_month: 1 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="name" label="Template Name" rules={[{ required: true }]}>
                <Input placeholder="e.g. Monthly Stationery" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="vendor_id" label="Vendor" rules={[{ required: true }]}>
                <Select showSearch placeholder="Select vendor"
                  filterOption={(i, o) => o?.children?.toLowerCase().includes(i.toLowerCase())}>
                  {(vendors || []).map(v => <Option key={v.id} value={v.id}>{v.firm_name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="firm_name" label="Firm Name" rules={[{ required: true }]}>
                <Select showSearch placeholder="Select firm"
                  filterOption={(i, o) => o?.children?.toLowerCase().includes(i.toLowerCase())}>
                  {(firm_names || []).map(f => <Option key={f.id} value={f.firm_name}>{f.firm_name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="delivery_address_id" label="Delivery Address">
                <Select showSearch placeholder="Select address"
                  filterOption={(i, o) => o?.children?.toLowerCase().includes(i.toLowerCase())}>
                  {(delivery_addresses || []).map(a => (
                    <Option key={a.id} value={a.id}>{a.unit_name || `${a.address}, ${a.city}`}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ fontSize: 13 }}>Schedule</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="frequency" label="Frequency" rules={[{ required: true }]}>
                <Select onChange={(v) => { setFrequency(v); }}>
                  <Option value="weekly">Weekly</Option>
                  <Option value="monthly">Monthly</Option>
                  <Option value="custom">Custom</Option>
                </Select>
              </Form.Item>
            </Col>
            {frequency === 'weekly' && (
              <Col xs={24} sm={8}>
                <Form.Item name="day_of_week" label="Day of Week">
                  <Select>
                    {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d, i) => (
                      <Option key={i} value={i}>{d}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            {frequency === 'monthly' && (
              <Col xs={24} sm={8}>
                <Form.Item name="day_of_month" label="Day of Month">
                  <InputNumber min={1} max={28} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            )}
            {frequency === 'custom' && (
              <Col xs={24} sm={8}>
                <Form.Item name="interval_days" label="Interval (days)">
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Divider orientation="left" style={{ fontSize: 13 }}>
            Items
            <Button size="small" type="dashed" onClick={() => setItems(p => [...p, { key: Date.now(), product_id: undefined, product_name: '', quantity: 1, rate: 0, tax_slab: '18', discount: 0 }])} style={{ marginLeft: 12 }}>
              + Add Row
            </Button>
          </Divider>

          {items.map((item, i) => (
            <Row gutter={8} key={item.key} style={{ marginBottom: 8 }} align="middle">
              <Col flex="auto">
                <Select showSearch placeholder="Product" style={{ width: '100%' }} size="small"
                  value={item.product_id}
                  onChange={(id) => {
                    const p = (products || []).find(x => x.id === id);
                    updateItem(item.key, 'product_id', id);
                    if (p) {
                      updateItem(item.key, 'product_name', p.product_name || p.name || '');
                      updateItem(item.key, 'rate', p.purchase_rate || 0);
                      updateItem(item.key, 'tax_slab', String(p.tax_slab || '18'));
                      updateItem(item.key, 'unit', p.unit || '');
                    }
                  }}
                  filterOption={(inp, opt) => opt?.children?.toLowerCase().includes(inp.toLowerCase())}
                >
                  {(products || []).map(p => <Option key={p.id} value={p.id}>{p.product_name || p.name}</Option>)}
                </Select>
              </Col>
              <Col style={{ width: 70 }}>
                <InputNumber size="small" style={{ width: '100%' }} min={1} placeholder="Qty"
                  value={item.quantity} onChange={v => updateItem(item.key, 'quantity', v)} />
              </Col>
              <Col style={{ width: 90 }}>
                <InputNumber size="small" style={{ width: '100%' }} min={0} placeholder="Rate"
                  value={item.rate} onChange={v => updateItem(item.key, 'rate', v)} prefix="₹" />
              </Col>
              <Col style={{ width: 32 }}>
                {items.length > 1 && (
                  <Button size="small" type="text" danger onClick={() => setItems(p => p.filter(x => x.key !== item.key))}>✕</Button>
                )}
              </Col>
            </Row>
          ))}
        </Form>
      </Modal>
    </div>
  );
};

export default RecurringPOList;
